// PhotoGalleryScreen.tsx
import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  TextInput,
} from "react-native";
import { TPatientInfo } from "../../../../services/api-clinic";
import { TGetPatientImageData } from "../../../../services/api";
import { CollapsibleSection } from "./CollapsibleSection";
import { PatientImageGrid } from "./PatientImageGrid";
import { usePhotoGalleryData } from "./UsePhotoGalleryData";
import global from "../../../../styles/globalStyles";
import { NormalText, TitleText } from "../../../common/native";
export interface GroupedImage extends TGetPatientImageData {
  patientInfo: TPatientInfo;
}
export interface DayGroup {
  date: string;
  patients: {
    [patientId: string]: {
      info: TPatientInfo;
      images: TGetPatientImageData[];
    };
  };
}
export interface MonthGroup {
  monthYear: string;
  days: { [day: string]: DayGroup };
}
export interface YearGroup {
  year: string;
  months: { [monthYear: string]: MonthGroup };
}

const PhotoGalleryScreen: React.FC = () => {
  const [searchText, setSearchText] = useState("");
  const { groupedData, loading, error, refreshData } = usePhotoGalleryData({
    query: searchText,
    mode: "auto", // auto-detect email/phone, default to fuzzy name
  });

  if (loading && Object.keys(groupedData).length === 0) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" />
        <Text style={[global.text, styles.loadingText]}>Loading gallery…</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.centerContainer}>
        <Text style={[global.text, styles.errorText]}>
          Error loading gallery: {error}
        </Text>
      </View>
    );
  }

  if (!groupedData || Object.keys(groupedData).length === 0) {
    return (
      <View style={styles.centerContainer}>
        <Text style={[global.text, styles.emptyText]}>
          No images found in gallery
        </Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TitleText
          text="Photo Gallery"
          style={{ marginTop: 5 }}
          numberOfLines={1}
        />

        <View style={styles.searchRow}>
          <TextInput
            value={searchText}
            onChangeText={setSearchText}
            placeholder="Search by name, email, or phone"
            style={styles.searchInput}
            returnKeyType="search"
            onSubmitEditing={() => refreshData()}
            clearButtonMode="while-editing"
          />
        </View>
      </View>

      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {Object.entries(groupedData)
          .sort(([a], [b]) => parseInt(b) - parseInt(a)) // years desc
          .map(([year, yearGroup]) => (
            <CollapsibleSection
              key={year}
              title={year}
              defaultCollapsed={false}
            >
              {Object.entries(yearGroup.months)
                .sort(([a], [b]) => {
                  const [monthA, yearA] = a.split(" ");
                  const [monthB, yearB] = b.split(" ");
                  const dateA = new Date(`${monthA} 1, ${yearA}`);
                  const dateB = new Date(`${monthB} 1, ${yearB}`);
                  return dateB.getTime() - dateA.getTime();
                })
                .map(([monthYear, monthGroup]) => (
                  <CollapsibleSection
                    key={monthYear}
                    title={monthYear}
                    defaultCollapsed={false}
                    level={1}
                  >
                    {Object.entries(monthGroup.days)
                      .sort(
                        ([a], [b]) =>
                          new Date(b).getTime() - new Date(a).getTime(),
                      )
                      .map(([date, dayGroup]) => (
                        <CollapsibleSection
                          key={date}
                          title={new Date(date).toLocaleDateString("en-US", {
                            weekday: "long",
                            month: "long",
                            day: "numeric",
                          })}
                          defaultCollapsed={true}
                          level={2}
                        >
                          {({ isVisible }) => (
                            <>
                              {Object.entries(dayGroup.patients).map(
                                ([patientId, patientData]) => (
                                  <View
                                    key={patientId}
                                    style={styles.patientSection}
                                  >
                                    <Text
                                      style={[
                                        global.text,
                                        global.fontWeight600,
                                        styles.patientName,
                                      ]}
                                    >
                                      {patientData.info.firstName}{" "}
                                      {patientData.info.lastName}
                                    </Text>
                                    <PatientImageGrid
                                      images={patientData.images}
                                      isVisible={isVisible}
                                      initialCount={24}
                                      onEditComplete={refreshData}
                                    />
                                  </View>
                                ),
                              )}
                            </>
                          )}
                        </CollapsibleSection>
                      ))}
                  </CollapsibleSection>
                ))}
            </CollapsibleSection>
          ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: "#fff" },
  header: { padding: 20, borderBottomWidth: 1, borderBottomColor: "#DDDEE0" },
  searchRow: {
    marginTop: 12,
    flexDirection: "row",
    gap: 8,
    alignItems: "center",
  },
  searchInput: {
    flex: 1,
    height: 40,
    paddingHorizontal: 12,
    borderWidth: 1,
    borderColor: "#DDDEE0",
    borderRadius: 8,
    backgroundColor: "#fff",
  },
  scrollView: { flex: 1, padding: 16 },
  centerContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  loadingText: { marginTop: 10, color: "#666" },
  errorText: { color: "#d32f2f", textAlign: "center" },
  emptyText: { color: "#666", textAlign: "center" },
  patientSection: {
    marginVertical: 8,
    padding: 12,
    backgroundColor: "#f8f9fa",
    borderRadius: 8,
  },
  patientName: { marginBottom: 8, color: "#333" },
});

export default PhotoGalleryScreen;
